import type { MonthlyResponse } from '@/shared/api/graphql/rpmReports';
import type { PatientsData } from '@/shared/lib/types/patients';
import type { AccountNoteType, NavigationBlockStatusType, RPMAccountAlert } from '@/shared/lib/types/remotePatientMonitoring';
import create from 'zustand';
import { DOCKED_PANEL_IDS, useDockedPanelStore } from './DockedPanelStore';

export type AccountStore = {
  account: PatientsData | null;
  setAccount: (account: PatientsData) => void;
  globalAccounts: PatientsData[];
  setGlobalAccounts: (value: PatientsData[]) => void;
  accountNotes: AccountNoteType[];
  setAccountNotes: (value: AccountNoteType[]) => void;
  notesRefetchToken: number;
  bumpNotesRefetch: () => void;
  isAccountChanged: boolean;
  setIsAccountChanged: (value: boolean) => void;
  showPatientReviewWindow: boolean;
  showAlertReviewFollowupWindow: boolean;
  alertReviewFollowupData: {
    selectedCriticalAlerts: RPMAccountAlert[];
  };
  navigationBlockStatus: NavigationBlockStatusType | null;
  setActivityLogged: (value: NavigationBlockStatusType | null) => void;
  resetActivityLogged: () => void;
  setShowPatientReviewWindow: (value: boolean) => void;
  setShowAlertReviewFollowupWindow: (value: boolean) => void;
  setAlertReviewFollowupData: (value: RPMAccountAlert[]) => void;
  patientReviewWorkflowMonthYear: string | null;
  setPatientReviewWorkflowMonthYear: (value: string | null) => void;
  patientReviewWorkflowId: string | null;
  setPatientReviewWorkflowId: (value: string) => void;
  isReviewWorkflowChanged: boolean;
  setIsReviewWorkflowChanged: (value: boolean) => void;
  reviewWorkflowMonth: MonthlyResponse | null;
  setReviewWorkflowMonth: (value: MonthlyResponse | null) => void;
};

export const useAccountStore = create<AccountStore>(set => ({
  account: null,
  setAccount: value => set(() => ({ account: value })),
  globalAccounts: [],
  setGlobalAccounts: value => set(() => ({ globalAccounts: value })),
  accountNotes: [],
  setAccountNotes: value => set(() => ({ accountNotes: value })),
  notesRefetchToken: 0,
  bumpNotesRefetch: () => set(s => ({ notesRefetchToken: s.notesRefetchToken + 1 })),
  isAccountChanged: false,
  setIsAccountChanged: value => set(() => ({ isAccountChanged: value })),
  showPatientReviewWindow: false,
  showAlertReviewFollowupWindow: false,
  alertReviewFollowupData: {
    selectedCriticalAlerts: []
  },
  navigationBlockStatus: null,
  setActivityLogged: value => set(() => ({ navigationBlockStatus: value })),
  resetActivityLogged: () => set(() => ({ navigationBlockStatus: null })),
  setShowPatientReviewWindow: value => set(() => ({ showPatientReviewWindow: value })),
  setShowAlertReviewFollowupWindow: value => {
    set(() => ({ showAlertReviewFollowupWindow: value }));
    // Opening an alert while it is already stacked behind another docked panel brings it forward.
    const docked = useDockedPanelStore.getState();
    if (value && docked.panels.some(panel => panel.id === DOCKED_PANEL_IDS.ALERT_REVIEW))
      docked.setActivePanel(DOCKED_PANEL_IDS.ALERT_REVIEW);
  },
  setAlertReviewFollowupData: value => set(() => ({ alertReviewFollowupData: { selectedCriticalAlerts: value } })),
  patientReviewWorkflowMonthYear: null,
  setPatientReviewWorkflowMonthYear: value => set(() => ({ patientReviewWorkflowMonthYear: value })),
  patientReviewWorkflowId: null,
  setPatientReviewWorkflowId: value => set(() => ({ patientReviewWorkflowId: value })),
  isReviewWorkflowChanged: false,
  setIsReviewWorkflowChanged: value => set(() => ({ isReviewWorkflowChanged: value })),
  reviewWorkflowMonth: null,
  setReviewWorkflowMonth: value => set(() => ({ reviewWorkflowMonth: value }))
}));
